import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { FC, Suspense } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Route, Routes } from 'react-router-dom';
import { NFCClock } from './components/clock/NFCClock';
import { NFCRedirect } from './components/clock/NFCRedirect';
import { CatastrophicErrorBoundary } from './components/errors/CatastrophicErrorBoundary';
import { FabricModalHost } from './components/fabric/Fabric';
import { Loading } from './components/shared/Loading';
import { AppInsightsRouter, reactPlugin } from './helpers/AppInsights';
import { NativeFontManager } from './helpers/native/NativeFontManager';
import { NotFound } from './pages/NotFound';
import { AdminPage } from './pages/admin/AdminPage';
import { AdminEditContractPage } from './pages/admin/contracts/edit/AdminEditContractPage';
import { AdminEditLeaveTypePage } from './pages/admin/leaveType/edit/AdminEditLeaveTypePage';
import { AdminEditLocationPage } from './pages/admin/locations/edit/AdminEditLocationPage';
import { AdminEditSkillPage } from './pages/admin/skills/edit/AdminEditSkillPage';
import { AdminEditTaskPage } from './pages/admin/tasks/edit/AdminEditTaskPage';
import { ClockInOutCancel } from './pages/clockInOut/ClockInOutCancel';
import { ClockInOutPageContainer } from './pages/clockInOut/ClockInOutContainer';
import { ClockInOutPage } from './pages/clockInOut/ClockInOutPage';
import { ClockInOutSuccess } from './pages/clockInOut/ClockInOutSuccess';
import { AllDevicesPage } from './pages/devices/AllDevicesPage';
import { ManageDevicePage } from './pages/devices/ManageDevicePage';
import MyAvailability from './pages/ess/myAvailability/MyAvailability';
import { MyCalendar } from './pages/ess/myCalendar/MyCalendar';
import { MyProfile } from './pages/ess/myProfile/MyProfile';
import { MyProfileEdit } from './pages/ess/myProfile/MyProfileEdit';
import MySchedule from './pages/ess/mySchedule/MySchedule';
import MyScheduleEdit from './pages/ess/mySchedule/MyScheduleEdit';
import ViewRoster from './pages/ess/viewRoster/ViewRoster';
import ViewShiftDetails from './pages/ess/viewShiftDetails/ViewShiftDetails';
import { HomePage } from './pages/home/HomePage';
import { ManageLocationPage } from './pages/manageLocations/ManageLocationPage';
import { ManageOrgPage } from './pages/manageOrg/ManageOrgPage';
import { OrganisationStructurePage } from './pages/manageOrg/structure/OrganisationStructurePage';
import { MyDataPage } from './pages/myData/MyData';
import { PrivacyPolicyPage } from './pages/myData/PrivacyPolicyPage';
import { RosterPage } from './pages/roster/RosterPage';
import { RosterCalendarPage } from './pages/roster/calendar/RosterCalendarPage';
import { OptimserPage } from './pages/roster/optimise/OptimserPage';
import { MapScribble } from './pages/scribble/MapScribble';
import { Scribbles } from './pages/scribble/Scribbles';
import { AllShiftsPage } from './pages/shifts/AllShiftsPage';
import { AllShiftsReport } from './pages/shifts/shiftReports/AllShiftsReport';
import { TeamCalendarPage } from './pages/team/teamCalendar/TeamCalendarPage';
import { AddTeamMemberPage } from './pages/team/teamMember/AddTeamMemberPage';
import { TeamMember } from './pages/team/teamMember/TeamMember';
import { TeamMemberCalendarDayView } from './pages/team/teamMember/calendar/TeamMemberCalendarDayView';
import TeamMemberCalendarDayViewEditAvailability from './pages/team/teamMember/calendar/TeamMemberCalendarDayViewEditAvailability';
import { TeamMemberDetailsEdit } from './pages/team/teamMember/overview/TeamMemberDetailsEdit';
import { TeamMemberSkillsEdit } from './pages/team/teamMember/skills/TeamMemberSkillsEdit';
import { TeamMemberPermissionsEdit } from './pages/team/teamMember/workRules/TeamMemberPermissionsEdit';
import { TeamMemberContractEdit } from './pages/team/teamMember/workRules/contract/TeamMemberContractEdit';
import { TeamMemberScheduleEdit } from './pages/team/teamMember/workRules/schedule/TeamMemberScheduleEdit';
import { TeamPage } from './pages/team/teamTable/TeamPage';
import { AddMultipleTeamMembers } from './pages/team/teamTable/addTeamMembers/AddMultipleTeamMembers';
import { TemplateEditorPage } from './pages/templates/TemplateEditorPage';
import { TemplatePage } from './pages/templates/TemplatePage';
import { TenantManagementPage } from './pages/tenantManagement/TenantManagementPage';
import { AdminTenantSettingsPage } from './pages/tenantSettings/AdminTenantSettingsPage';
import { SuperAdminTenantSettingsPage } from './pages/tenantSettings/SuperAdminTenantSettingsPage';
import { Authenticated } from './services/authentication/routes/AuthenticatedRoute';
import { GetStartedPage } from './services/authentication/routes/GetStarted';
import { Login } from './services/authentication/routes/Login';
import { Logout } from './services/authentication/routes/Logout';
import { PostLogout } from './services/authentication/routes/PostLogout';
import { Redirect } from './services/authentication/routes/Redirect';
import { RequireActingLocation } from './services/authentication/routes/RequireActingLocation';
import { RequireAdmin } from './services/authentication/routes/RequireAdmin';
import { RequireDebug } from './services/authentication/routes/RequireDebug';
import { RequireManager } from './services/authentication/routes/RequireManager';
import { RequireSuperAdmin } from './services/authentication/routes/RequireSuperAdmin';
import { BackstageProvider } from './services/backstage/BackstageProvider';
import { ConfirmCheck } from './services/confirm/ConfirmCheck';
import { ConfirmationProvider } from './services/confirm/ConfirmationService';
import { URQLProvider } from './services/gql/config/URQLProvider';
import { DeviceLocationProvider } from './services/location/useDeviceLocation';
import { ToastService } from './services/notification/ToastService';

const App: FC = () => {
  return (
    <Suspense fallback={<Loading />}>
      <AppInsightsRouter>
        <DndProvider backend={HTML5Backend}>
          <CatastrophicErrorBoundary>
            <DeviceLocationProvider>
              <BackstageProvider>
                <URQLProvider>
                  <ConfirmationProvider>
                    <ConfirmCheck>
                      <ToastService>
                        <NativeFontManager />
                        <FabricModalHost />
                        <Suspense fallback={<Loading />}>
                          <Routes>
                            <Route element={<Authenticated fullWidth />}>
                              <Route element={<RequireActingLocation />}>
                                <Route element={<RequireManager />}>
                                  <Route path="/team/calendar" element={<TeamCalendarPage />} />
                                </Route>
                                <Route path="rosters/:date" element={<RosterPage />} />
                              </Route>
                              <Route path="/" element={<HomePage />} />
                            </Route>
                            <Route element={<Authenticated />}>
                              <Route element={<RequireActingLocation />}>
                                <Route element={<RequireManager />}>
                                  <Route path="/team" element={<TeamPage />} />
                                  <Route path="/team/add-team-member" element={<AddTeamMemberPage />} />
                                  <Route path="/team-member/:userId" element={<TeamMember />} />
                                  <Route path="/team-member/:userId/edit-details" element={<TeamMemberDetailsEdit />} />
                                  <Route
                                    path="/team-member/:userId/edit-details/:scrollPayIntoView"
                                    element={<TeamMemberDetailsEdit />}
                                  />
                                  <Route
                                    path="/team-member/:userId/set-schedule"
                                    element={<TeamMemberScheduleEdit />}
                                  />
                                  <Route
                                    path="/team-member/:userId/edit-permissions"
                                    element={<TeamMemberPermissionsEdit />}
                                  />
                                  <Route
                                    path="/team-member/:userId/edit-contract"
                                    element={<TeamMemberContractEdit />}
                                  />
                                  <Route
                                    path="/team-member/:userId/calendar-details/:date"
                                    element={<TeamMemberCalendarDayView />}
                                  />
                                  <Route
                                    path="/team-member/:userId/calendar-details/:date/edit-availability"
                                    element={<TeamMemberCalendarDayViewEditAvailability />}
                                  />
                                  <Route path="/team-member/:userId/set-skills" element={<TeamMemberSkillsEdit />} />
                                  <Route path="/team-member/:userId/:tabName" element={<TeamMember />} />
                                  <Route path="/team/add-multiple" element={<AddMultipleTeamMembers />} />
                                  <Route path="/shifts/report" element={<AllShiftsReport />} />
                                  <Route path="/shifts" element={<AllShiftsPage />}>
                                    <Route path=":recordId" element={<AllShiftsPage />}>
                                      <Route path=":shiftStart" element={<AllShiftsPage />} />
                                    </Route>
                                  </Route>
                                  <Route path="rosters" element={<RosterCalendarPage />}></Route>
                                  <Route path="rosters/calendar/:date" element={<RosterCalendarPage />}></Route>
                                  <Route path="rosters/optimise" element={<OptimserPage />} />
                                  <Route path="templates" element={<TemplatePage />} />
                                  <Route path="templates/:templateId" element={<TemplateEditorPage />} />
                                </Route>
                              </Route>
                              <Route element={<ClockInOutPageContainer />}>
                                <Route path="/clock-in-out" element={<ClockInOutPage />} />
                                <Route path="/clock-in-out/:deviceId/:qrCode" element={<ClockInOutPage />} />
                                <Route path="/clock-in-cancel" element={<ClockInOutCancel />} />
                                <Route path="/clock-in-out-success/:state" element={<ClockInOutSuccess />} />
                              </Route>
                              <Route path="/my-data" element={<MyDataPage />} />
                              <Route path="/ess">
                                <Route path="my-profile" element={<MyProfile />}></Route>
                                <Route path="my-profile/edit" element={<MyProfileEdit />}></Route>
                                <Route path="my-calendar" element={<MyCalendar />}></Route>
                                <Route path="my-calendar/:date/my-availability" element={<MyAvailability />}></Route>
                                <Route path="my-schedule" element={<MySchedule />}></Route>
                                <Route path="my-schedule/edit" element={<MyScheduleEdit />}></Route>
                                <Route path="my-calendar/:date/view-roster" element={<ViewRoster />}></Route>
                                <Route path="my-calendar/:date/shift-details" element={<ViewShiftDetails />}></Route>
                              </Route>
                              <Route path="/NC1yMzRsLWg0Y2st8Jkpg" element={<NFCRedirect />} />
                              <Route path="/nfc" element={<NFCClock />} />
                              <Route element={<RequireSuperAdmin />}>
                                <Route path="/tenant-management" element={<TenantManagementPage />} />
                                <Route path="/superadmin-tenant-settings" element={<SuperAdminTenantSettingsPage />} />
                              </Route>
                              <Route element={<RequireAdmin />}>
                                <Route path="/admin/:tabName?" element={<AdminPage />} />
                                <Route
                                  path="/admin/locations/:locationId/:tabName?"
                                  element={<AdminEditLocationPage />}
                                />
                                <Route
                                  path="/admin/leave-types/:leaveTypeId/:tabName?"
                                  element={<AdminEditLeaveTypePage />}
                                />
                                <Route path="/admin/skills/:skillId/:tabName?" element={<AdminEditSkillPage />} />
                                <Route path="/admin/tasks/:taskId/:tabName?" element={<AdminEditTaskPage />} />
                                <Route
                                  path="/admin/contracts/:contractId/:tabName?"
                                  element={<AdminEditContractPage />}
                                />
                                <Route path="/admin-tenant-settings" element={<AdminTenantSettingsPage />} />
                                <Route path="/manage-organisation" element={<ManageOrgPage />} />
                                <Route path="/manage-organisation/structure" element={<OrganisationStructurePage />} />
                              </Route>
                              <Route element={<RequireManager />}>
                                <Route path="/manage-location/:locationId" element={<ManageLocationPage />} />
                                <Route path="/manage-location/:locationId/devices" element={<AllDevicesPage />} />
                                <Route
                                  path="/manage-location/:locationId/devices/:deviceId"
                                  element={<ManageDevicePage />}
                                />
                              </Route>
                              <Route element={<RequireDebug />}>
                                <Route path="/scribbles" element={<Scribbles />} />
                                {/* ToDo cant use old calendar stuff */}
                                {/* <Route path="/scribbles/calendar" element={<CalendarScribble />} /> */}
                                <Route path="/scribbles/map" element={<MapScribble />} />
                              </Route>
                            </Route>
                            <Route path="/get-started" element={<GetStartedPage />} />
                            <Route path="/login" element={<Login />} />
                            <Route path="/logout" element={<Logout />} />
                            <Route path="/logout/callback" element={<PostLogout />} />
                            <Route path="/signin-oidc" element={<Redirect />} />
                            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                            <Route path="*" element={<NotFound />} />
                          </Routes>
                        </Suspense>
                      </ToastService>
                    </ConfirmCheck>
                  </ConfirmationProvider>
                </URQLProvider>
              </BackstageProvider>
            </DeviceLocationProvider>
          </CatastrophicErrorBoundary>
        </DndProvider>
      </AppInsightsRouter>
    </Suspense>
  );
};

export default withAITracking(reactPlugin, App);
